import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { NameStep } from "@/components/auth/NameStep.tsx";
import { VerificationCodeInput } from "@/components/auth/VerificationCodeInput.tsx";
import { Button } from "@/components/ui/button.tsx";
import { currentUserQuery } from "@/hooks/useUser.tsx";
import { clearClientStorage, endSession } from "@/lib/endSession.ts";
import { api } from "@/lib/http.ts";
import type { Account } from "@/types/accounts.ts";
import type {
  ClaimLinkAcceptResp,
  ClaimLinkInfoResp,
  ClaimLinkVerifyReq,
  ClaimLinkVerifyResp,
} from "@/types/claimLink.ts";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

/**
 * The claim landing: a guest tapped the link okthx texted or emailed them. The
 * account already exists and the link was delivered to the number or address
 * that is its identity, so Claim signs them in. Past the link's window the
 * same button sends a code to that number, which signs them in instead, or a
 * fresh link to that address.
 *
 * An address is not proof of the person, so a guest signed in by an emailed
 * link lands in the app only to verify a phone, which the onboarding guard
 * asks for on its own.
 *
 * Signing in is not the end of the page: a guest's card carries the number a
 * roster offered in place of a name, so the name is asked here, as a signed-in
 * person, before the app opens on a setup thread that greets them by it.
 *
 * The link carries the token in the URL fragment, which a browser never sends,
 * and every API call carries it in a POST body, so it never lands in a request
 * log. The page only ever sees the number masked.
 */
export const Claim = () => {
  const [token] = useState(readClaimToken);
  const { info, accept, verify, landing, claimed } = useClaimLink(token);

  if (!token && !landing) return null;

  const renderContent = () => {
    // Dead on the server's word only. A check that failed to reach the server
    // says nothing about the link, and must not hide it behind an older claim.
    const linkDead = !token || (info.isSuccess && info.data.status === "error");
    // Signed in by either door, or back on the page with a name still to give:
    // the name step, unless a live link is open, since a link is what the
    // person came here to use. A dead link with a claim to finish is not a
    // dead end; the name step is what is left to do.
    if (landing && (claimed || linkDead)) return <ClaimNameStep landing={landing} />;
    if (!token) return null;
    if (info.isPending) return <p className="text-muted-foreground">Loading...</p>;
    if (info.isError)
      return <Problem message="Something went wrong. Please reload to try again." />;
    if (info.data.status === "error") {
      return (
        <Problem message="This link is not valid. Reply to Okthx in your group to get a new one." />
      );
    }
    if (accept.isError || (accept.data && accept.data.status === "error")) {
      return <Problem message="Something went wrong. Please try again." />;
    }
    const { identityMasked, sponsorNames, expired } = info.data;
    // An API that predates email links sends no type, and only phone links then.
    const claimAction = describeClaimAction(expired, info.data.identityType === "email");
    if (accept.data?.status === "link-sent") {
      return (
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">Check your inbox</h1>
          <p className="text-muted-foreground">
            Check {identityMasked} for the newest link from Okthx, and claim your account from
            there.
          </p>
        </div>
      );
    }
    if (accept.data?.status === "code-sent") {
      return (
        <CodeForm
          identityMasked={identityMasked}
          verify={verify}
          onSubmit={(code) => verify.mutate({ token, code })}
          onResend={() => accept.mutate()}
        />
      );
    }
    if (accept.isPending) return <p className="text-muted-foreground">Signing you in...</p>;
    return (
      <>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">Claim your Okthx account</h1>
          <p className="text-muted-foreground">
            {describeSponsors(sponsorNames)} This account is for {identityMasked}.
          </p>
          {expired && <p className="text-muted-foreground">{claimAction.note}</p>}
        </div>
        <Button
          onClick={() => accept.mutate()}
          className="w-full"
          data-statsig-name={claimAction.statsigName}
        >
          {claimAction.label}
        </Button>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 px-6 text-center">
        <OkthxLogo className="size-12" />
        {renderContent()}
      </div>
    </div>
  );
};

const tokenStorageKey = "okthx.claim-token";
const landingStorageKey = "okthx.claim-landing";

/**
 * Take the token out of the fragment and off the URL in the same breath: the
 * fragment never reaches a server, but it is still in this document, where the
 * error tracker captures the current URL and where it would sit in history and
 * in anything copied from the address bar. It is kept in this tab's
 * sessionStorage so a refresh still lands here; signing in wipes that storage.
 */
const readClaimToken = (): string | null => {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (token) window.history.replaceState(null, "", window.location.pathname);
  try {
    if (token) window.sessionStorage.setItem(tokenStorageKey, token);
    return token ?? window.sessionStorage.getItem(tokenStorageKey);
  } catch {
    return token;
  }
};

/** Where the app opens once the name is in: the setup thread, when there is one. */
type Landing = { onboardingThreadId: number | null };

/**
 * The name step for a claimed guest, offering the name the account already
 * carries. The session is set by now, so the account answers for itself. A
 * roster sometimes gives a real name (a WhatsApp sender's display name), and
 * that is worth confirming rather than retyping; the number standing in for
 * one is not a name and is not offered.
 */
const ClaimNameStep = ({ landing }: { landing: Landing }) => {
  const user = useQuery({ ...currentUserQuery(), retry: false });
  if (user.isPending) return <p className="text-muted-foreground">Loading...</p>;
  return (
    <NameStep
      statsigName="claim-set-name"
      initialName={user.data ? nameWorthOffering(user.data.user) : undefined}
      onSaved={() => landInApp(landing)}
    />
  );
};

/**
 * The card's name, unless it is one of the card's own phone numbers, just
 * digits, or an address: a guest minted from an email thread with no name on
 * it carries its address in place of one.
 */
const nameWorthOffering = (user: Account) => {
  const { name, handles } = user.userContact;
  const trimmed = name.trim();
  if (trimmed === "" || trimmed.includes("@")) return undefined;
  const phones = (handles ?? []).filter((h) => h.type === "phoneNumber").map((h) => h.value);
  if (phones.includes(trimmed) || /^[+\d\s().-]+$/.test(trimmed)) return undefined;
  return trimmed;
};

/**
 * Where a claim that has already signed this tab in was going, if it has not
 * got there yet. The link is spent the moment it signs someone in, and its
 * token goes with the storage wipe, so a refresh on the name step has only
 * this to pick up from. Cleared with the rest of the tab's storage when the
 * app opens.
 */
const readClaimLanding = (): Landing | null => {
  try {
    const stored = window.sessionStorage.getItem(landingStorageKey);
    if (stored === null) return null;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null || !("onboardingThreadId" in parsed)) {
      return null;
    }
    const { onboardingThreadId } = parsed;
    return typeof onboardingThreadId === "number" || onboardingThreadId === null
      ? { onboardingThreadId }
      : null;
  } catch {
    return null;
  }
};

/**
 * The three calls the landing makes, all carrying the link token: what the
 * link is for, redeeming it, and verifying the code an expired link sent.
 * Either mutation signing in sets the session cookie on its response and
 * records where to land; the page then asks for the name before going there.
 */
const useClaimLink = (token: string | null) => {
  const info = useQuery(claimInfoQuery(token));
  const [landing, setLanding] = useState<Landing | null>(readClaimLanding);
  // Whether this page signed someone in, as opposed to finding an unfinished
  // claim in storage: the link has just been spent, so the name step follows
  // whatever the link's own state reads as.
  const [claimed, setClaimed] = useState(false);

  /**
   * The identity in this browser has just changed. Whatever the account that
   * used it before left in storage goes now, not once the name saves: the name
   * step can be abandoned, and the app can be opened by any other route on the
   * session the response just set. The token goes with it, which is right: the
   * link is spent, and the landing kept here is what a refresh resumes from.
   */
  const signedIn = (onboardingThreadId: number | null) => {
    clearClientStorage();
    const next: Landing = { onboardingThreadId };
    try {
      window.sessionStorage.setItem(landingStorageKey, JSON.stringify(next));
    } catch {
      // Storage disabled: a refresh loses the step, as it loses the token.
    }
    setLanding(next);
    setClaimed(true);
  };

  const accept = useMutation({
    mutationFn: async (): Promise<ClaimLinkAcceptResp> => {
      const res = await api.post<ClaimLinkAcceptResp>("/api/p/claim/accept", { token });
      return res.data;
    },
    onSuccess: (data) => {
      if (data.status === "claimed") signedIn(data.onboardingThreadId);
    },
  });
  const verify = useMutation({
    mutationFn: async (req: ClaimLinkVerifyReq): Promise<ClaimLinkVerifyResp> => {
      const res = await api.post<ClaimLinkVerifyResp>("/api/p/claim/verify-code", req);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.status === "claimed") signedIn(data.onboardingThreadId);
    },
  });
  return { info, accept, verify, landing, claimed };
};

const claimInfoQuery = (token: string | null) =>
  queryOptions({
    queryKey: ["claim", "info", token] as const,
    queryFn: async ({ signal }) => {
      const res = await api.post<ClaimLinkInfoResp>("/api/p/claim/info", { token }, { signal });
      return res.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

/**
 * The session cookie is set; a full navigation picks it up. Whatever identity
 * this browser held before is replaced, so its storage ends with it.
 */
const landInApp = ({ onboardingThreadId }: Landing) => {
  endSession(onboardingThreadId === null ? "/" : `/threads/${onboardingThreadId}`);
};

/**
 * What the Claim button does: sign in, or, past the link's window, send a
 * code to the number or a fresh link to the address.
 */
const describeClaimAction = (
  expired: boolean,
  byEmail: boolean,
): { label: string; note: string; statsigName: string } => {
  if (!expired) return { label: "Claim account", note: "", statsigName: "claim-account" };
  if (byEmail) {
    return {
      label: "Send me a new link",
      note: "This link has expired. We can email a new one to that address.",
      statsigName: "claim-resend-link",
    };
  }
  return {
    label: "Text me a code",
    note: "This link has expired. We can text a code to that number instead.",
    statsigName: "claim-account",
  };
};

const describeSponsors = (names: string[]): string => {
  if (names.length === 0) return "Your group is using Okthx.";
  if (names.length === 1) return `${names[0]} is using Okthx.`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} are using Okthx.`;
};

type VerifyMutation = ReturnType<
  typeof useMutation<ClaimLinkVerifyResp, Error, ClaimLinkVerifyReq>
>;

const describeVerifyFailure = (verify: VerifyMutation): string => {
  if (verify.isError) return "Something went wrong. Please try again.";
  switch (verify.data?.status) {
    case "invalid-code":
      return "Invalid code. Please try again.";
    case "error":
      return verify.data.message;
    default:
      return "";
  }
};

/** A dead end: the link cannot be used, with one line saying what to do instead. */
const Problem = ({ message }: { message: string }) => (
  <div className="flex flex-col gap-2">
    <h1 className="text-2xl font-semibold">Something went wrong</h1>
    <p className="text-muted-foreground">{message}</p>
  </div>
);

/**
 * The six-digit code form for an expired link. Submits the code with the
 * link token; the server knows which number the code went to.
 */
const CodeForm = ({
  identityMasked,
  verify,
  onSubmit,
  onResend,
}: {
  identityMasked: string;
  verify: VerifyMutation;
  onSubmit: (code: string) => void;
  onResend: () => void;
}) => {
  // Typing again hides the last failure until the next attempt answers.
  const [cleared, setCleared] = useState(false);
  const failure = cleared ? "" : describeVerifyFailure(verify);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Enter the code</h1>
        <p className="text-muted-foreground">We texted a code to {identityMasked}.</p>
      </div>
      <VerificationCodeInput
        onComplete={(code) => {
          setCleared(false);
          onSubmit(code);
        }}
        disabled={verify.isPending}
        error={failure}
        onErrorClear={() => setCleared(true)}
      />
      <Button
        variant="outline"
        onClick={onResend}
        className="w-full"
        data-statsig-name="claim-resend-code"
      >
        Resend code
      </Button>
    </div>
  );
};
